import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import CustomAutocomplete from '../../../autocomplete-field';
import axios from 'axios';
import DynamicTable from '../../../Dynamic-table';
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from 'react-toastify';
import { isDateTimeGreaterThan30MinutesAgo } from '../../../../utils/dateTimeUtils'; 
import debounce from 'lodash.debounce';
import dayjs from 'dayjs';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkLabReportsDialog, { LinkedLabReport, approvalSummary, fetchPatientSamples } from './LinkLabReportsDialog';
import PreviewPDF from '../../../../pages/preview';

export const Investigation: React.FC<any> = ({ onChange, data, profile, opdData, otherTabsData }) => {
    const [selectedTests, setSelectedTests] = useState<any[]>([]);
    const [selectedInvestigationDetails, setSelectedInvestigationDetails] = useState<any[]>([]);
    const [newTestInput, setNewTestInput] = useState("");
    const [procTests, setProcTests] = useState<any>();
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [testToAdvise, setTestToAdvise] = useState<any>(null); 
    const [opdTests, setOpdTests] = useState<any>();
    const [linkedLabReports, setLinkedLabReports] = useState<LinkedLabReport[]>([]);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [labPreviewData, setLabPreviewData] = useState<any>(null);
    const [showLabPreview, setShowLabPreview] = useState(false);
    useEffect(() => {
        setOpdTests(opdData?.tests)
    }, [opdData]);

    const debouncedFetchProcedureTests = useCallback(
        debounce(async (search) => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_OPD_API_URL}/procedure/getAllTests?count=50000&search=${search}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    }
                );
                const fetchedTests = response.data?.data || [];
                const uniqueDiagnosis = Array.from(
                    new Map(fetchedTests?.map((item: any) => [item?.testName, item]))?.values()
                );
                setProcTests(uniqueDiagnosis);
            } catch (error) {
                console.error("Error fetching procedures:", error);
            }
        }, 500),
        []
    );

    useEffect(() => {
        debouncedFetchProcedureTests(newTestInput);
    }, [debouncedFetchProcedureTests, newTestInput]);

    useEffect(() => {
        onChange({
            investigation: selectedInvestigationDetails,
            opd_Tests: opdTests,
            linked_Lab_Reports: linkedLabReports,
        });
    }, [selectedInvestigationDetails, opdTests, linkedLabReports]);
    useEffect(() => {
        setLinkedLabReports(Array.isArray(data?.linked_Lab_Reports) ? data.linked_Lab_Reports : []);
    }, [data]);

    const handleLinkSample = (sample: any) => {
        setLinkedLabReports(prev => [
            ...prev,
            {
                sampleCollectionId: sample?._id,
                trNo: sample?.trNo,
                sampleDate: sample?.createdAt,
                collectionCenterName: sample?.collectionCenter?.collectionCenterName || '',
                tests: (sample?.testResults || []).map((t: any) => ({
                    _id: t?._id,
                    testName: t?.testName,
                    approvalStatus: t?.approvalStatus,
                })),
                linkedAt: dayjs().format('DD.MM.YYYY hh:mm A'),
                linkedBy: profile?.data?.fullName,
            },
        ]);
        toast.success(`Lab sample TR# ${sample?.trNo} linked.`);
    };

    const handleUnlinkSample = (sampleCollectionId: string) => {
        setLinkedLabReports(prev => prev.filter(r => r.sampleCollectionId !== sampleCollectionId));
        toast.success('Lab report unlinked.');
    };

    // Approval status may have changed since linking, so re-fetch then open preview directly.
    const handleViewReport = async (report: LinkedLabReport) => {
        try {
            const [sample] = await fetchPatientSamples({
                fromDate: dayjs(report.sampleDate).format('YYYY-MM-DD'),
                toDate: dayjs().format('YYYY-MM-DD'),
                searchBy: 'TRNo',
                search: String(report.trNo),
            });
            const approvedIds = (sample?.testResults || [])
                .filter((t: any) => t?.approvalStatus === 'Approved')
                .map((t: any) => t?._id);
            if (!sample || approvedIds.length === 0) {
                toast.info('No approved test results yet for this sample.');
                return;
            }
            const response = await axios.post(
                `${process.env.REACT_APP_LAB_URL}/patient-result/printTestResults`,
                {
                    historyCount: 3,
                    sampleCollectionId: sample._id,
                    performedTestIds: approvedIds,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );
            if (response.status === 201 && response?.data?.data) {
                setLabPreviewData(response.data.data);
                setShowLabPreview(true);
            } else {
                toast.error(response?.data?.message || 'Failed to generate lab report preview.');
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Failed to load lab report.');
        }
    };
    useEffect(() => {
        if (Array.isArray(data?.investigation)) {
            const sortedInvestigationDetails = data?.investigation?.sort((a: any, b: any) =>
                a?.departmentName?.localeCompare(b?.departmentName)
            );
            setSelectedInvestigationDetails(sortedInvestigationDetails);
        } else {
            setSelectedInvestigationDetails([]);
        }
    }, [data]);

    // useEffect(() => {
    //     if (data?.investigation) {
    //         const sortedInvestigationDetails = (data?.investigation || [])?.sort((a: any, b: any) =>
    //             a?.departmentName?.localeCompare(b?.departmentName)
    //         );
    //         setSelectedInvestigationDetails(sortedInvestigationDetails);
    //     }
    // }, [data]);

    // const handleTestsChange = (event: React.ChangeEvent<{}>, newValue: any[]) => {
    //     if (!newValue) return;
    //     const currentDateTime = dayjs();

    //     const newInvestigations = newValue?.filter((item: any) =>
    //         !opdTests?.some((opdTest: any) =>
    //             opdTest?.testName === item?.testName && opdTest?.procedureName === item?.procedureName
    //         )
    //     );
    //     const duplicates = newValue?.filter((item: any) =>
    //         opdTests?.some((opdTest: any) =>
    //             opdTest?.testName === item?.testName && opdTest?.procedureName === item?.procedureName
    //         )
    //     );
    //     if (duplicates.length > 0) {
    //         setTestToAdvise(duplicates[0]);
    //         setDialogOpen(true);
    //         return;
    //     }
    //     if (newInvestigations?.length > 0) {
    //         setSelectedInvestigationDetails((prevDetails: any[]) => {
    //             const newDetails = newInvestigations?.map((item: any) => ({
    //                 department_Name: item?.procedureName || '',
    //                 investigation_Name: item?.testName || '',
    //                 date_Time: currentDateTime?.format('DD.MM.YYYY hh:mm A') || '',
    //                 doctor_Name: profile?.data?.fullName
    //             }));
    //             const allDetails = [...prevDetails, ...newDetails];
    //             allDetails?.sort((a, b) => a?.department_Name?.localeCompare(b?.departmentName));
    //             return allDetails;
    //         });
    //     }
    // };

    const handleTestsChange = (event: React.ChangeEvent<{}>, newValue: any[]) => {
        if (!newValue) return;

        const currentDateTime = dayjs();
        const newInvestigations = newValue?.filter((item: any) =>
            !opdTests?.some((opdTest: any) =>
                opdTest?.testName === item?.testName && opdTest?.procedureName === item?.procedureName
            ) && !selectedInvestigationDetails?.some((existingItem: any) =>
                existingItem?.investigation_Name === item?.testName && existingItem?.department_Name === item?.procedureName
            )
        );
        const duplicates = newValue?.filter((item: any) =>
            opdTests?.some((opdTest: any) =>
                opdTest?.testName === item?.testName && opdTest?.procedureName === item?.procedureName
            )
        );
        const alreadySelectedTests = newValue?.filter((item: any) =>
            selectedInvestigationDetails?.some((existingItem: any) =>
                existingItem?.investigation_Name === item?.testName && existingItem?.department_Name === item?.procedureName
            )
        );

        if (alreadySelectedTests.length > 0) {
            toast.info("This test is already selected.");
            return; 
        }
        if (duplicates.length > 0) {
            setTestToAdvise(duplicates[0]);
            setDialogOpen(true);
            return;
        }

        if (newInvestigations?.length > 0) {
            setSelectedInvestigationDetails((prevDetails: any[]) => {
                const newDetails = newInvestigations?.map((item: any) => ({
                    department_Name: item?.procedureName || '',
                    investigation_Name: item?.testName || '',
                    date_Time: currentDateTime?.format('DD.MM.YYYY hh:mm A') || '',
                    doctor_Name: profile?.data?.fullName
                }));
                const allDetails = [...prevDetails, ...newDetails];
                allDetails?.sort((a, b) => a?.department_Name?.localeCompare(b?.department_Name)); 
                return allDetails;
            });
        }

        setNewTestInput("")
    };

    const handleDeleteInvestigation = (rowIndex: number) => {
        setSelectedInvestigationDetails((prevDetails) => {
            const updatedDetails = [...prevDetails];
            updatedDetails?.splice(rowIndex, 1);
            toast.success("Investigation removed successfully!");
            return updatedDetails;
        });
    };

    const handleDialogClose = (confirmed: boolean) => {
        setDialogOpen(false);
        if (confirmed && testToAdvise) {
            const currentDateTime = dayjs(); 
            setSelectedInvestigationDetails((prevDetails: any[]) => {
                const newDetails = [{
                    department_Name: testToAdvise?.procedureName || '',
                    investigation_Name: testToAdvise?.testName || '',
                    date_Time: currentDateTime.format('DD.MM.YYYY hh:mm A') || '',
                    doctor_Name: profile?.data?.fullName,
                }];
                const allDetails = [...prevDetails, ...newDetails];
                allDetails?.sort((a, b) => a?.department_Name?.localeCompare(b?.departmentName));
                return allDetails;
            });
        }
    };

    return (
        <>
            <Box sx={{ overflowY: 'auto' }}>
                <Grid container spacing={3}>
                    <Grid item md={4} sm={6} xs={12}>
                        <CustomAutocomplete
                            multiple
                            getOptionLabel={(option) => option?.testName || ""}
                            filterOptions={(options, { inputValue }) =>
                                options?.filter((option: any) =>
                                    option?.testName?.toLowerCase().includes(inputValue.toLowerCase())
                                )
                            }
                            options={procTests || []}
                            value={selectedTests}
                            onChange={handleTestsChange}
                            renderOption={(props, option: any) => (
                                <li {...props}>
                                    <Typography sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        {option.testName || ""}
                                        <span style={{ fontSize: "10px", color: "gray" }}>
                                            {option?.procedureName ? `(${option?.procedureName})` : ""}
                                        </span>
                                    </Typography>
                                </li>
                            )}
                            placeholder="Select Tests"
                            onTextFieldChange={(event) => setNewTestInput(event.target.value)}
                            handleBlur={() => setNewTestInput("")}
                        />
                    </Grid>
                    <Grid item md={8} sm={6} xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                        <Typography sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}>
                            <b>OPD Investigations:</b>  {opdTests?.map((test: any) => test?.testName)?.join(', ')}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<LinkIcon />}
                            onClick={() => setLinkDialogOpen(true)}
                            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                            Link Lab Reports
                        </Button>
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid item md={12} sm={12} xs={12}>
                        <Box mt={1.5}>
                            <DynamicTable
                                headers={[
                                    "SN",
                                    "Date & Time",
                                    "Department",
                                    "Investigation",
                                    "Given By",
                                    "Action",
                                ]}
                                data={selectedInvestigationDetails?.map((item: any, index: number) => [
                                    index + 1,
                                    item.date_Time,
                                    item?.department_Name,
                                    item?.investigation_Name,
                                    item?.doctor_Name,
                                ])}
                                actionButtons={[
                                    {
                                        icon: (rowIndex: number) => (
                                            <DeleteIcon
                                                sx={{
                                                    color: isDateTimeGreaterThan30MinutesAgo(selectedInvestigationDetails[rowIndex]?.date_Time) ? "#FF5454" : "#D3D3D3",
                                                    "&:hover": {
                                                        background: "none",
                                                        color: isDateTimeGreaterThan30MinutesAgo(selectedInvestigationDetails[rowIndex]?.date_Time) ? "rgb(191, 44, 44)" : "#D3D3D3",
                                                    },
                                                }}
                                            />
                                        ),
                                        onClick: (rowIndex: number) => handleDeleteInvestigation(rowIndex),
                                        disabled: (rowIndex: number) => !isDateTimeGreaterThan30MinutesAgo(selectedInvestigationDetails[rowIndex]?.date_Time),
                                    },
                                ]}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                    <DialogTitle>Test Already Advised</DialogTitle>
                    <DialogContent>
                        <Typography>Do you want to advise <b>{testToAdvise?.testName}</b> again?</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => handleDialogClose(false)} color="primary">No</Button>
                        <Button onClick={() => handleDialogClose(true)} color="primary">Yes</Button>
                    </DialogActions>
                </Dialog>
            </Box>

            {linkedLabReports.length > 0 && (
                <Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, mb: 1 }}>Linked Lab Reports</Typography>
                    <DynamicTable
                        height="auto"
                        headers={["SN", "TR No", "Sample Date", "Tests", "Status", "Linked On", "Linked By", "Action"]}
                        data={linkedLabReports.map((report, index) => [
                            index + 1,
                            report.trNo,
                            dayjs(report.sampleDate).format('DD.MM.YYYY hh:mm A'),
                            report.tests?.map(t => t.testName).join(', '),
                            approvalSummary(report.tests),
                            report.linkedAt,
                            report.linkedBy,
                        ])}
                        actionButtons={[
                            {
                                icon: <Tooltip title="View Report"><VisibilityIcon sx={{ color: "#1976D2" }} /></Tooltip>,
                                onClick: (rowIndex: number) => handleViewReport(linkedLabReports[rowIndex]),
                            },
                            {
                                icon: <Tooltip title="Unlink"><LinkOffIcon sx={{ color: "#FF5454" }} /></Tooltip>,
                                onClick: (rowIndex: number) => handleUnlinkSample(linkedLabReports[rowIndex].sampleCollectionId),
                            },
                        ]}
                    />
                </Box>
            )}

            <LinkLabReportsDialog
                open={linkDialogOpen}
                onClose={() => setLinkDialogOpen(false)}
                mrn={otherTabsData?.patientData?.mrn}
                linkedReports={linkedLabReports}
                onLink={handleLinkSample}
                onUnlink={handleUnlinkSample}
            />
            {showLabPreview && labPreviewData && (
                <PreviewPDF
                    print={true}
                    data={labPreviewData}
                    onClose={() => {
                        setShowLabPreview(false);
                        setLabPreviewData(null);
                    }}
                />
            )}
        </>
    );
};
